import { useNavigate, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { v4 } from 'uuid';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import { Button } from '@mui/material';
import { PageLayout } from '../../PageLayout';
import { DetailViewCard } from '../../Compounds/CardWithTitle/DetailViewCard';
import { DetailPanelContainer } from '../../Compounds/DetailPanel/DetailPanelContainer';
import { EditFieldType, LabelValueField } from '../../Compounds/DetailPanel/LabelValueField';
import { ComponentName, getMetaData } from '../../Compounds/DetailsDrawer/detailsDrawersHelpers';
import { addDrawer, getFromDrawerSubject, updateDrawerOpenSubject } from '../../../store/Drawer';
import { Link, fetchLink, getLinkSubject, updateLink } from '../../../store/Link';
import { getUserSubject, User, UserRole } from '../../../store/User';
import { getAssetById } from '../../../store/Asset';
import { getProvisionalAssetById } from '../../../store/ProvisionalAsset';
import { getRpmAccountById } from '../../../store/RpmAccount';
import { linkSectionsInfo } from './linkHelpers';
import { ComponentSpinner } from '../../Compounds/Loading/ComponentSpinner';
import { getFormattedDate } from '../../../utils/helpers';
import { LinkAllocationSection } from './LinkAllocationSection';

export const LinkDetailPage = () => {
  const { id } = useParams();
  const [data, setData] = useState<Link>({} as Link);
  const [linkLoading, setLinkLoading] = useState(false);
  const navigate = useNavigate();
  const [user, setUser] = useState<User>();

  useEffect(() => {
    const userSub = getUserSubject().subscribe((u) => setUser(u));

    return () => {
      if (userSub) userSub.unsubscribe();
    };
  }, []);

  const handleLoadData = async () => {
    if (id) {
      setLinkLoading(true);
      const errorStatusCode = await fetchLink(id);
      if (errorStatusCode) navigate(`/error/${errorStatusCode}/link/${id}/${new Date().getTime()}`);
      setLinkLoading(false);
    }
  };

  const handleBack = () => {
    if (getFromDrawerSubject().getValue()) {
      addDrawer({
        id: v4(),
        data,
        ...getMetaData({ name: ComponentName.LINK, id: data.id || '', data })
      });
    }
    window.history.length > 1 ? navigate(-1) : navigate('/link');
  };

  useEffect(() => {
    const linkSub = getLinkSubject().subscribe((a) => {
      setData(a);
    });

    return () => {
      if (linkSub) linkSub.unsubscribe();
    };
  }, []);

  useEffect(() => {
    handleLoadData();
  }, [id]);

  const pageActions = () => {
    return (
      <Button
        color="secondary"
        variant="outlined"
        startIcon={<ArrowBackIosIcon />}
        onClick={handleBack}>
        Back
      </Button>
    );
  };

  const saveAction = (update: Link) => updateLink(update);

  const handleDetailClick = async (type: string) => {
    if (type === 'rpmAccount') {
      const rpmAccount = await getRpmAccountById(String(data.rpmAccountId));
      addDrawer({
        id: v4(),
        type: 'rpmAccount',
        ...getMetaData({ name: ComponentName.RPM_ACCOUNT, id: String(data.rpmAccountId) }),
        data: rpmAccount
      });
    } else if (type === 'asset') {
      const asset = await getAssetById(data.assetId || '');
      addDrawer({
        id: v4(),
        type: 'asset',
        ...getMetaData({
          name: ComponentName.ASSET,
          sfId: data.assetSfId,
          id: data.assetId || '',
          data: data.assetId ? asset : undefined
        }),
        data: asset
      });
    } else if (type === 'provisionalAsset') {
      const provisionalAsset = await getProvisionalAssetById(data.assetId || '');
      addDrawer({
        id: v4(),
        type: 'provisionalAsset',
        ...getMetaData({ name: ComponentName.PROVISIONAL_ASSET, sfId: data.assetSfId, id: data.assetId || '' }),
        data: provisionalAsset
      });
    }
    updateDrawerOpenSubject(true);
  };

  const updateStatus = async (status: string): Promise<string | null> => {
    let candidate = data.candidate || false;
    let archived = data.archived || false;
    if (status === 'Active') {
      candidate = false;
      archived = false;
    } else if (status === 'Candidate') {
      candidate = true;
      archived = false;
    } else if (status === 'Archived') {
      archived = true;
    }
    await saveAction({ ...data, archived, candidate });
    return null;
  };

  return (
    <PageLayout
      className="mb-10"
      dataTestId="link-detail-page"
      subTitle="Link"
      pageTitle={`${data.archived ? 'Archived' : data.candidate ? 'Candidate' : 'Active'} Link`}
      key={data.id}
      showArchived={data.archived}
      pageActions={pageActions()}>
      {linkLoading && (
        <ComponentSpinner
          sx={{ m: 'auto', height: '100%' }}
          label="Loading Data..."
        />
      )}
      {!linkLoading && data && (
        <div className="grid grid-cols-3 mt-6 space-x-6">
          <div className="space-y-6">
            <DetailViewCard
              enableCollapse
              key="link_detail"
              title="Info">
              <div>
                <div className="w-full flex flex-wrap pt-4 px-4">
                  <div className="w-1/2 flex flex-col mb-1 pr-9 -mb-2">
                    <p className="font-sans text-grey-6 text-[14px] font-normal">
                      {data.assetSfId ? 'Asset' : 'Provisional Asset'}
                    </p>
                    <button
                      className="text-indigo"
                      data-testid="detail-cta-button"
                      onClick={() => handleDetailClick(data.assetSfId ? 'asset' : 'provisionalAsset')}
                      style={{
                        background: 'none',
                        border: 'none',
                        padding: 0,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        fontSize: '14px',
                        textAlign: 'left'
                      }}>
                      {data.assetSupplierName}: {data.assetNumber}
                    </button>
                  </div>
                  <div className="w-1/2 flex flex-col mb-1 pr-9 -mb-2">
                    <p className="font-sans text-grey-6 text-[14px] font-normal">RPM Account</p>
                    <button
                      className="text-indigo"
                      data-testid="detail-cta-button"
                      onClick={() => handleDetailClick('rpmAccount')}
                      style={{
                        background: 'none',
                        border: 'none',
                        padding: 0,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        fontSize: '14px',
                        textAlign: 'left'
                      }}>
                      {data.rpmAccountSupplierName}: {data.rpmAccountNumber}
                    </button>
                  </div>
                </div>
                <DetailPanelContainer
                  data={linkSectionsInfo(data)}
                  saveAction={(d) => saveAction(d as Link)}
                />
              </div>
            </DetailViewCard>
            <LinkAllocationSection
              link={data}
              key={data.id}
            />
          </div>
          <div className="space-y-6">
            <DetailViewCard
              enableCollapse
              key="links_detail"
              title="Links">
              <div className="flex px-4 pt-4 w-full" />
            </DetailViewCard>
          </div>
          <div className="space-y-6">
            <DetailViewCard
              enableCollapse
              key="meta_detail"
              title="Meta">
              <div className="flex px-4 pt-4 w-full">
                <LabelValueField
                  label="ID"
                  value={data.id || ''}
                  field="id"
                />
                {[UserRole.ADMIN, UserRole.ENGINEER].includes(user?.role as UserRole) ? (
                  <LabelValueField
                    label="Status"
                    value={data.archived ? 'Archived' : data.candidate ? 'Candidate' : 'Active'}
                    field="status"
                    canEdit={{
                      field: 'status',
                      type: EditFieldType.SELECT,
                      options: ['Active', 'Candidate', 'Archived']
                    }}
                    saveValueAction={(val, _previewValue) => updateStatus(String(val))}
                  />
                ) : (
                  <LabelValueField
                    label="Status"
                    value={data.archived ? 'Archived' : data.candidate ? 'Candidate' : 'Active'}
                    field="status"
                  />
                )}
              </div>
              <div className="flex px-4 w-full">
                <LabelValueField
                  label="Created At"
                  value={getFormattedDate(data.createdAt, 'lll')}
                  field="createdAt"
                />
                <LabelValueField
                  label="Modified At"
                  value={getFormattedDate(data.updatedAt, 'lll')}
                  field="updatedAt"
                />
              </div>
              <div className="flex px-4 w-full">
                <LabelValueField
                  label="Created By"
                  value={`${data.createdBy?.firstName || ''} ${data.createdBy?.lastName || ''}`}
                  field="createdBy.firstName"
                />
              </div>
            </DetailViewCard>
          </div>
        </div>
      )}
    </PageLayout>
  );
};
